import React from 'react';
import { Button, Icon, Typography } from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../../../shared/hooks';
import ThemeToggle from '../ThemeToggle/ThemeToggle';

import ComplianceScoreChip from './ComplianceScoreChip';
import type { HeaderProps } from './type';

import './Header.css';

/**
 * License Intelligence dashboard header displaying the page title,
 * last data sync timestamp, compliance score badge, and action buttons for
 * adding licenses and widgets.
 * @param {HeaderProps} props - Component props
 * @param {number} [props.complianceScore] - Compliance score percentage shown as a badge
 * @param {string} [props.lastSyncDate] - Formatted date string for the last data sync
 * @param {() => void} [props.onAddLicense] - Callback fired when the "Add License" button is clicked
 * @param {() => void} [props.onAddWidget] - Callback fired when the "Add Widget" button is clicked
 * @returns {React.ReactElement} The rendered header section
 */
function Header({
  complianceScore,
  lastSyncDate,
  onAddLicense,
  onAddWidget,
}: HeaderProps): React.ReactElement {
  const localize = useInvertedLocalize();

  return (
    <div className='licenseHeader'>
      {/* Top Row: Title and Sync Info */}
      <div className='licenseHeaderTopRow'>
        <Typography kind='h1'>{localize('License Intelligence')}</Typography>

        <div className='licenseHeaderSyncSection'>
          <div className='licenseHeaderSyncText'>
            <Typography kind='body3'>
              {localize('Last data sync:')}
              <span className='licenseHeaderSyncDate'>{lastSyncDate}</span>
            </Typography>
          </div>

          <Icon
            kind='AlertInfoFilled'
            size='small'
            className='licenseHeaderInfoIcon'
          />
          <ThemeToggle />
        </div>
      </div>

      {/* Bottom Row: Compliance Score and Action Buttons */}
      <div className='licenseHeaderBottomRow'>
        {complianceScore !== undefined && (
          <ComplianceScoreChip
            score={complianceScore}
            label={localize('Compliance Score')}
          />
        )}

        <div className='licenseHeaderActionsSection'>
          {onAddLicense && (
            <Button
              className='licenseHeaderAddLicenseBtn'
              onClick={onAddLicense}>
              <div className='licenseHeaderAddLicenseBtnContent'>
                <Icon
                  kind='Add'
                  size='small'
                  className='licenseHeaderAddLicenseIcon'
                />
                <Typography kind='body2-bold'>
                  {localize('Add License')}
                </Typography>
              </div>
            </Button>
          )}

          {onAddWidget && (
            <Button className='licenseHeaderAddWidgetBtn' onClick={onAddWidget}>
              <div className='licenseHeaderAddWidgetBtnContent'>
                <Icon
                  kind='Add'
                  size='small'
                  className='licenseHeaderAddWidgetIcon'
                />
                <Typography kind='body2-bold'>
                  {localize('Add Widget')}
                </Typography>
              </div>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Header;
